import { ChatConversation, ChatMessage, SSEEvent, SSEEventType } from '@/features/chat/types';
import Cookies from 'js-cookie';
import { chatApi } from '.';
import { configSettings } from '@/lib/config';
import { useAuthStore } from '@/stores';

export async function fetchConversations(): Promise<ChatConversation[]> {
    const token = Cookies.get('token');
    if (!token) throw new Error('No authentication token found');
    const { data: conversations } = await chatApi.get<ChatConversation[]>(`/v1/chat/conversations`);
    return conversations as ChatConversation[];
}

export async function fetchConversationHistory(conversationId: string): Promise<ChatMessage[]> {
    const { data: history } = await chatApi.get<ChatMessage[]>(`/v1/chat/conversations/${conversationId}/messages`);
    return history as ChatMessage[];
}

export async function deleteConversation(conversationId: string): Promise<void> {
    await chatApi.delete(`/v1/chat/conversations/${conversationId}`);
}

export async function renameConversation(conversationId: string, title: string): Promise<ChatConversation> {
    return (await chatApi.put(`/v1/chat/conversations/${conversationId}/title`, { title })) as ChatConversation;
}

export interface TitleUpdateCallbacks {
    onTitleUpdate: (conversationId: string, title: string) => void;
}

export function listenForTitleUpdates(conversationId: string, callbacks: TitleUpdateCallbacks) {
    const token = useAuthStore.getState().token;

    if (!token) {
        throw new Error('No authentication token found');
    }

    const url = `${configSettings.apiUrl}/v1/chat/conversations/${conversationId}/events`;

    const controller = new AbortController();

    const startSSE = async () => {
        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'text/event-stream',
                'Cache-Control': 'no-cache',
            },
            signal: controller.signal,
        });

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) return;

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');

            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    const data = line.slice(6);
                    const sseEvent: SSEEvent = JSON.parse(data);

                    if (sseEvent.type === SSEEventType.TITLE_UPDATED && sseEvent.data?.title) {
                        callbacks.onTitleUpdate(sseEvent.conversation_id || conversationId, sseEvent.data.title);
                        controller.abort();
                        return;
                    }
                }
            }
        }
    };

    startSSE();

    // Auto-cleanup: 10 minutes for dev, 30 seconds for production
    const timeout = configSettings.isDevelopment ? 600000 : 30000;
    setTimeout(() => {
        controller.abort();
    }, timeout);

    return { abort: () => controller.abort() };
}
